import { useCallback, useMemo } from 'react'

import {
  CustomFieldType,
  type CustomFieldSchemaInput,
} from '@tribeplatform/gql-client/types'
import {
  useAddPostTypeField,
  useUpdatePostTypeField,
} from '@tribeplatform/react-sdk/hooks'
import type { ColoredLabel } from '@tribeplatform/react-ui-kit/LabelPicker'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { CustomFieldSubtype } from '../../CustomField/utils.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import {
  MemberGroups,
  ReadOptions,
  WriteOptions,
  type InputProps,
} from '../types.js'
import {
  convertToSelectOptions,
  getFieldItems,
  getFieldSetting,
  getReadPermissionOption,
  getReadPrivacyValue,
  getColoredLabels,
  getValidators,
  getWritePermissionOption,
  getWritePrivacyValue,
  slugifyFieldKey,
} from '../utils/utils.js'

interface HandleSubmitArgs {
  name: CustomFieldSchemaInput['name']
  key: CustomFieldSchemaInput['key']
  required: CustomFieldSchemaInput['required']
  placeholder?: string
  helperText?: string
  positiveLabel?: string
  negativeLabel?: string
  readPermission: ReadOptions
  writePermission: WriteOptions
  memberGroup?: MemberGroups
  options?: ColoredLabel[]
}

interface Props {
  field: CustomFieldSchemaInput
  isNew: boolean
  postTypeId: string
  goBack: () => void
  goToFields?: () => void
}
export const useFieldSettings = ({
  field,
  isNew,
  postTypeId,
  goToFields,
  goBack,
}: Props) => {
  const { $t } = useI18n()
  const { mutateAsync: addField, isLoading: isCreating } = useAddPostTypeField()
  const { mutateAsync: updateField, isLoading: isUpdating } =
    useUpdatePostTypeField()
  const {
    name,
    type,
    key,
    typeOptions,
    required,
    items,
    readPrivacy,
    writePrivacy,
    default: fieldDefaultValue,
  } = field
  const subtype = getFieldSetting<CustomFieldSubtype>(field, 'subtype')
  const placeholder = getFieldSetting<string>(field, 'placeholder')
  const helperText = getFieldSetting<string>(field, 'helperText')
  const inputProps = getFieldSetting<InputProps>(field, 'inputProps')
  const { trueText: positiveLabel, falseText: negativeLabel } =
    getFieldSetting<{ trueText: string; falseText: string }>(
      field,
      'viewProps',
    ) ?? {}
  const options = getColoredLabels(field)
  const memberGroup = inputProps?.memberGroup ?? MemberGroups.all

  const defaultValues = useMemo<HandleSubmitArgs>(() => {
    if (isNew) {
      const values: HandleSubmitArgs = {
        name,
        key: slugifyFieldKey(name),
        required: true,
        placeholder,
        helperText,
        positiveLabel,
        negativeLabel,
        readPermission: ReadOptions.everyone,
        writePermission: WriteOptions.own,
        ...(subtype === CustomFieldSubtype.MEMBER ||
        subtype === CustomFieldSubtype.MEMBERS
          ? { memberGroup: MemberGroups.all }
          : {}),
        ...(subtype === CustomFieldSubtype.SELECT ||
        subtype === CustomFieldSubtype.MULTISELECT
          ? { options: [] }
          : {}),
      }
      return values
    }

    const values: HandleSubmitArgs = {
      name,
      key,
      required,
      placeholder,
      helperText,
      positiveLabel,
      negativeLabel,
      readPermission: getReadPermissionOption(readPrivacy?.allow),
      writePermission: getWritePermissionOption(writePrivacy?.allow),
      ...(subtype === CustomFieldSubtype.MEMBER ||
      subtype === CustomFieldSubtype.MEMBERS
        ? { memberGroup }
        : {}),
      ...(subtype === CustomFieldSubtype.SELECT ||
      subtype === CustomFieldSubtype.MULTISELECT
        ? { options }
        : {}),
    }
    return values
  }, [
    helperText,
    isNew,
    key,
    memberGroup,
    name,
    negativeLabel,
    options,
    placeholder,
    positiveLabel,
    readPrivacy?.allow,
    required,
    subtype,
    writePrivacy?.allow,
  ])

  const handleSubmit = useCallback(
    async ({
      name,
      key,
      required,
      placeholder,
      helperText,
      positiveLabel,
      negativeLabel,
      readPermission,
      writePermission,
      memberGroup,
      options,
    }: HandleSubmitArgs) => {
      const readPrivacyValue = getReadPrivacyValue(readPermission)
      const writePrivacyValue = getWritePrivacyValue(writePermission)
      const validators =
        subtype === CustomFieldSubtype.SELECT ? getValidators(options) : []
      const fieldItems = getFieldItems(subtype, items, options)
      const defaultValue =
        (fieldDefaultValue ?? type === CustomFieldType.boolean)
          ? JSON.stringify(false)
          : undefined
      const subtypeSettings = subtype
        ? [
            {
              key: 'subtype',
              value: JSON.stringify(subtype),
            },
          ]
        : []
      const placeholderSettings = placeholder
        ? [
            {
              key: 'placeholder',
              value: JSON.stringify(placeholder),
            },
          ]
        : []
      const helperTextSettings = helperText
        ? [
            {
              key: 'helperText',
              value: JSON.stringify(helperText),
            },
          ]
        : []
      const viewPropsSettings =
        positiveLabel || negativeLabel
          ? [
              {
                key: 'viewProps',
                value: JSON.stringify({
                  trueText: positiveLabel,
                  falseText: negativeLabel,
                }),
              },
            ]
          : []
      const inputPropsSettings = memberGroup
        ? [
            {
              key: 'inputProps',
              value: JSON.stringify({ memberGroup }),
            },
          ]
        : []
      const selectOptionsSettings = options
        ? [
            {
              key: 'selectOptions',
              value: JSON.stringify(convertToSelectOptions(options)),
            },
          ]
        : []

      if (isNew) {
        try {
          await addField({
            id: postTypeId,
            input: {
              key,
              name,
              type,
              typeOptions,
              ...(defaultValue ? { default: defaultValue } : {}),
              settings: [
                ...subtypeSettings,
                ...placeholderSettings,
                ...helperTextSettings,
                ...viewPropsSettings,
                ...inputPropsSettings,
                ...selectOptionsSettings,
              ],
              validators,
              writePrivacy: { allow: writePrivacyValue },
              readPrivacy: { allow: readPrivacyValue },
              required,
              ...(fieldItems ? { items: fieldItems } : {}),
            },
          })
          toast({
            title: $t({
              defaultMessage: 'Field successfully added',
              id: 'Admin.CMSField.Add.Success',
            }),
            status: 'success',
          })
          goToFields()
        } catch (error) {
          toast({
            title: $t({
              defaultMessage: 'Could not add field',
              id: 'Admin.CMSField.Add.Error',
            }),
            status: 'error',
          })
        }

        return
      }

      try {
        await updateField({
          id: postTypeId,
          input: {
            key,
            name,
            settings: [
              ...subtypeSettings,
              ...placeholderSettings,
              ...helperTextSettings,
              ...viewPropsSettings,
              ...inputPropsSettings,
              ...selectOptionsSettings,
            ],
            validators,
            writePrivacy: { allow: writePrivacyValue },
            readPrivacy: { allow: readPrivacyValue },
            ...(defaultValue ? { default: defaultValue } : {}),
            required,
            ...(fieldItems ? { items: fieldItems } : {}),
          },
        })
        toast({
          title: $t({
            defaultMessage: 'Field successfully updates',
            id: 'Admin.CMSField.Update.Success',
          }),
          status: 'success',
        })
        goBack()
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Could not update field',
            id: 'Admin.CMSField.Update.Error',
          }),
          status: 'error',
        })
      }
    },
    [
      $t,
      addField,
      fieldDefaultValue,
      goBack,
      goToFields,
      isNew,
      items,
      postTypeId,
      subtype,
      type,
      typeOptions,
      updateField,
    ],
  )

  return {
    defaultValues,
    type,
    subtype,
    isCreating,
    isUpdating,
    handleSubmit,
  }
}
